import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Slide } from '@mui/material';
import useToastContext from '@/hooks/useToastContext';
import { Validations } from '@/types';
import styles from './ScrollValidationIndicator.module.scss';

type ScrollValidationIndicatorProps = {
  containerId: string;
  headerId: string;
  validations: Validations;
};

const ScrollValidationIndicator: React.FC<ScrollValidationIndicatorProps> = (props) => {
  const { containerId, headerId, validations } = props;
  const container: HTMLElement | null = document.getElementById(containerId);
  const crHeader: HTMLElement | null = document.getElementById(headerId);
  const { t } = useTranslation();
  const { numberOfToastsVisible } = useToastContext();
  const [indicatorVisible, setIndicatorVisible] = useState(false);
  const [topOfContainer, setTopOfContainer] = useState((container?.offsetHeight || 0) - 50);
  const [firstElementNotVisible, setFirstElementNotVisible] = useState<HTMLElement | null>(null);
  const [isTop, setIsTop] = useState(false);

  const onScroll = useCallback(() => {
    if (container) {
      const fields = document.getElementsByClassName('ValidationIndicatorField');
      const headerheight = crHeader?.offsetHeight || 0;
      const notVisible = Array.from(fields).every((field) => {
        const rect = field.getBoundingClientRect();
        const isVisible = (rect.top - headerheight) >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

        if (!isVisible) {
          setFirstElementNotVisible(field as HTMLElement);
          setIsTop((rect.top - headerheight) < 0);
        }

        return !isVisible;
      });
      setIndicatorVisible(fields.length > 0 ? notVisible : false);
      setTopOfContainer((container.offsetHeight || 0) - 50); // keep fixed position of indicator
    }
  }, [container]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIndicatorClick = () => {
    if (container) {
      firstElementNotVisible?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  useEffect(() => {
    container?.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);
    onScroll();

    return () => {
      container?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onScroll();
  }, [window.location.href, validations]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Slide direction="up" in={indicatorVisible} mountOnEnter unmountOnExit>
      <div
        className={styles.ScrollValidationIndicator}
        onClick={handleIndicatorClick}
        style={{ top: `${(topOfContainer - (numberOfToastsVisible * 50))}px` }}
      >
        <div className={styles.ScrollIndicator}>
          <i className={`di icon-auge-sichtbarkeit-an ${styles.Icon} `} />
          <p>{t('validations.someFieldNotVisible')}</p>
          <i className={`di icon-pfeil-chevron-${isTop ? 'oben' : 'unten'} ${styles.Icon} ${styles.IconArrow} `} />
        </div>
      </div>
    </Slide>
  );
};

export default ScrollValidationIndicator;
